import TextField from '@mui/material/TextField';
import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';

import { LiveSearchBoxProps } from './LiveSearchBox.props';
import { StyledAutoComplete } from './LiveSearchBox.styles';
import { SizeProps } from '../../../@types';
import { isEmptyString } from '../../../utils/common';

/**
 * MUI Autocomplete fires onChange for non-selection reasons too (e.g. reason==='clear'
 * when the field is emptied while re-searching), passing no option. Only forward a genuine
 * selection to the consumer — most callers read fields straight off the value (obj.code,
 * obj.id, …) and would crash on undefined. The cleared case is handled via onClearValue.
 */
export const forwardSelectedOption = (
  details: { option?: unknown } | undefined,
  onChange: (value: unknown) => void
): void => {
  if (details?.option !== undefined) {
    onChange(details.option);
  }
};

export const LiveSearchBox: React.FC<LiveSearchBoxProps> = ({
  title,
  timeOffset,
  isError,
  value,
  inputRef,
  isRequired,
  helperText,
  defaultSuggetions,
  disabled,
  feature,
  multiline = value ? true : false,
  onClearValue,
  onChange,
  onApiInvoke,
  renderItem,
  onKeyDown,
  isOptionEqualToValue,
  size = SizeProps.Small,
  groupBy,
  renderGroup,
  freeSolo,
  onClick,
  disableDelayedInput,
  byPassDefaultSuggestions = false,
  hideInlineLabel = false,
  placeholder,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; LiveSearchBox accepts heterogeneous item shapes from many callers
  const [options, setOptions] = useState<Array<any>>([]);
  const [inputValue, setInputValue] = useState('');
  const [helperTextValue, setHelperTextValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [delayedInput, setDelayedInput] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>(undefined);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    if (defaultSuggetions && defaultSuggetions.length) {
      setOptions(defaultSuggetions);
    }
  }, [defaultSuggetions]);

  useEffect(() => {
    if (delayedInput) {
      if (
        inputValue &&
        (!defaultSuggetions || defaultSuggetions?.length == 0) &&
        options.length === 1
      ) {
        onChange(options[0]);
        setIsOpen(false);
        setIsLoading(false);
      }

      setDelayedInput(false);
    }
    // Intentionally keyed only on `options`: this auto-selects a sole search result the
    // moment results arrive. Adding inputValue/onChange/defaultSuggetions would re-run it
    // on unrelated renders and re-fire the selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    if (helperText) setHelperTextValue(helperText);
  }, [helperText]);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; MUI Autocomplete onInputChange event is loosely typed
  const handleInputChange = (event: any, textValue: string) => {
    if (event?.nativeEvent?.inputType === 'insertFromPaste') {
      textValue = textValue.trim();
    }
    setInputValue(textValue);
    clearTimeout(timerRef.current);
    delayedInput && setDelayedInput(false);

    if (textValue?.length > 0) {
      setHelperTextValue('');
      setIsLoading(true);
      setOptions([]);
      const currentSeq = ++requestSeqRef.current;
      timerRef.current = setTimeout(async () => {
        try {
          const result = await onApiInvoke(textValue);
          if (currentSeq !== requestSeqRef.current) return;
          setIsLoading(false);
          setOptions(
            defaultSuggetions && defaultSuggetions.length && !byPassDefaultSuggestions
              ? [...defaultSuggetions, ...result]
              : result
          );
        } catch {
          if (currentSeq !== requestSeqRef.current) return;
          setIsLoading(false);
          setOptions([]);
        }
      }, timeOffset);
    } else if (isRequired) {
      requestSeqRef.current++;
      setIsLoading(false);
      setHelperTextValue(helperText || '');
      setOptions(defaultSuggetions && defaultSuggetions.length ? defaultSuggetions : []);
      onClearValue();
    } else {
      requestSeqRef.current++;
      setIsLoading(false);
      setOptions(defaultSuggetions && defaultSuggetions.length ? defaultSuggetions : []);
      onClearValue();
    }
  };

  const noOptionsText = inputValue.length === 0 ? 'Start typing to search…' : 'No matches';

  return (
    <StyledAutoComplete
      title={title}
      disabled={disabled}
      open={isOpen}
      freeSolo={freeSolo}
      renderOption={(props, option, state) => renderItem(props, option, state)}
      renderInput={(params) => (
        <TextField
          {...params}
          multiline={multiline}
          label={hideInlineLabel ? undefined : title}
          placeholder={placeholder}
          inputProps={{ ...params.inputProps, ...(hideInlineLabel ? { 'aria-label': title } : {}) }}
          disabled={disabled}
          helperText={helperTextValue}
          inputRef={inputRef}
          onClick={onClick}
          InputProps={{
            ...params.InputProps,
          }}
          error={isError || !isEmptyString(helperTextValue)}
        />
      )}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; MUI Autocomplete onChange signature is heterogeneous
      onChange={(event: React.SyntheticEvent, value: any, reason: string, details: any) =>
        forwardSelectedOption(details, onChange)
      }
      defaultValue={value}
      inputValue={inputValue}
      value={value}
      onInputChange={handleInputChange}
      options={options}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; options are heterogeneous across callers
      getOptionLabel={(obj: any) =>
        (feature === 'part-action' ? obj.serial1 || obj.name : obj.name) || value
      }
      filterOptions={(items) => items}
      loading={isLoading}
      noOptionsText={noOptionsText}
      size={size}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !disableDelayedInput) {
          setDelayedInput(true);
        }
        onKeyDown && onKeyDown(event, inputValue);
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      autoHighlight={!defaultSuggetions || defaultSuggetions?.length == 0}
      onBlur={() => {
        setIsLoading(false);
        setDelayedInput(false);
      }}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      groupBy={groupBy}
      renderGroup={renderGroup}
    />
  );
};
